<template>
  <div class="about">
    <!-- Hero -->
    <section class="about__hero container">
      <p class="eyebrow reveal">2001 — 2017 · in memoriam</p>
      <h1 class="about__title reveal" style="animation-delay:.05s">
        Il était une fois<br><span class="hl">labanane.org</span>
      </h1>
      <p class="about__lead reveal" style="animation-delay:.12s">
        « Le site qui vous colle la banane. La vraie, la dure, celle qui&nbsp;dure. »
      </p>
    </section>

    <!-- Story -->
    <section class="container story">
      <div class="story__block reveal">
        <span class="story__marker">🍌</span>
        <h2>Un site culte, une communauté</h2>
        <p>
          <strong>labanane.org</strong> était un répertoire collaboratif de blagues en français :
          chacun pouvait proposer les siennes, et c'était la communauté qui votait pour élire
          les plus drôles — et se moquer gentiment des « méga bides ». Tout était rangé en cinq
          familles devenues cultes : <em>Humour Carambar, Noir, Gras, Douteux et Absurde</em>.
          Le site tournait déjà en <strong>2001</strong> : un pur produit de la première
          génération du web francophone, bricolée, généreuse et un peu potache. Pour toute une
          génération, c'était <em>le</em> réflexe pour rire un bon coup entre deux cours ou
          deux réunions.
        </p>
      </div>

      <div class="story__block reveal">
        <span class="story__marker">💀</span>
        <h2>Puis, un jour, plus rien</h2>
        <p>
          Sans explication, sans annonce, sans au revoir. En remontant les archives, on peut
          presque dater le moment : jusqu'à fin 2016, les blagues étaient bien là. Quelque part
          au tournant de <strong>2017</strong>, elles se sont volatilisées. Il ne restait plus,
          sur la page d'accueil, que le petit avertissement légal et une coquille vide — la trace
          muette d'un serveur qui avait rendu l'âme. Figé, identique, pendant des années. Un
          fantôme. Puis, en <strong>2024</strong>, le domaine s'est éteint pour de bon. Vingt ans
          de fous rires, effacés d'un web qui oublie vite.
        </p>
      </div>

      <div class="story__block reveal">
        <span class="story__marker">🛟</span>
        <h2>Alors on a sauvé ce qu'on pouvait</h2>
        <p>
          Puisque le site vivant n'existait plus, nous sommes allés le déterrer dans la mémoire
          d'Internet : la <strong>Wayback Machine</strong> d'archive.org, qui avait photographié
          labanane des centaines de fois entre 2001 et 2024. Page après page, snapshot après
          snapshot, nous avons récupéré <strong>{{ totalLabel }} blagues</strong>, remises
          proprement dans leurs cinq catégories d'origine. Un vrai travail de fourmi, moitié
          archéologie, moitié acte d'amour. Sans la Wayback Machine, tout ça aurait bel et bien
          disparu — merci à ceux qui archivent le web pour nous tous.
        </p>
      </div>

      <div class="story__block reveal">
        <span class="story__marker">👑</span>
        <h2>Le score historique, préservé tel quel</h2>
        <p>
          Chaque blague affiche aujourd'hui deux choses : les <strong>votes actuels</strong> des
          visiteurs de passage… et, juste à côté, sa petite <strong>couronne 👑</strong>. Cette
          couronne, c'est le <strong>score historique</strong> — la note que la blague avait à
          l'époque sur le site original, figée telle quelle. Une trace intacte de sa popularité
          d'origine, sauvegardée avec le reste. Certaines cartes cachaient de vraies stars : la
          couronne vous dit lesquelles faisaient déjà pleurer de rire la communauté d'antan.
        </p>
      </div>
    </section>

    <!-- Credit / homage -->
    <section class="container">
      <div class="homage reveal">
        <h2>Préservation, pas appropriation.</h2>
        <p>
          Soyons clairs : <strong>ces blagues ne nous appartiennent pas.</strong> Elles ont été
          écrites, proposées, votées et aimées par toute une communauté d'anonymes, autour de
          l'équipe qui animait le site original. Ce que vous lisez ici est un <strong>hommage</strong>
          et un <strong>travail de préservation</strong> : rallumer la lumière sur un petit
          patrimoine de l'humour français qui, sans ça, se serait éteint pour de bon. On ne
          revendique rien — on garde la mémoire allumée.
        </p>
        <div class="homage__contact">
          <span>🤝</span>
          <p>
            Un des auteurs d'origine ou un ayant droit souhaite nous contacter — pour un mot, un
            crédit, ou le retrait d'un contenu&nbsp;? <strong>La porte est grande ouverte&nbsp;:</strong>
            écrivez-nous à <a class="homage__mail" href="mailto:contact@labanane.net">contact@labanane.net</a>.
          </p>
        </div>
        <p class="homage__sign">
          En attendant, on fait ce qui nous semble juste : ne pas laisser mourir ce qui nous a tant
          fait rire.<br><strong>Bienvenue sur la banane — épluchez-vous bien.</strong>
        </p>
      </div>
    </section>

    <!-- Timeline -->
    <section class="container tl">
      <h3 class="tl__head">La banane en quelques dates</h3>
      <ul class="tl__list">
        <li class="reveal"><span class="tl__year">2001</span><span class="tl__evt">Premier passage archivé. Le site existe déjà.</span></li>
        <li class="reveal"><span class="tl__year">2008</span><span class="tl__evt">Le site tourne à plein régime, porté par sa communauté de contributeurs.</span></li>
        <li class="reveal"><span class="tl__year">2016</span><span class="tl__evt">Apogée : ~2 800 blagues. Dernières archives où le contenu vit encore.</span></li>
        <li class="reveal tl--dead"><span class="tl__year">2017</span><span class="tl__evt">Panne silencieuse. Les blagues disparaissent. Le site devient une coquille vide.</span></li>
        <li class="reveal"><span class="tl__year">2024</span><span class="tl__evt">Le domaine finit par s'éteindre pour de bon.</span></li>
        <li class="reveal tl--revival"><span class="tl__year">2026</span><span class="tl__evt">🍌 Revival. Les blagues sont déterrées de la Wayback Machine. La banane repousse.</span></li>
      </ul>
    </section>

    <section class="container about__cta">
      <BananaButton to="/" size="lg" variant="dark">🍌 Découvrir les blagues</BananaButton>
    </section>
  </div>
</template>

<script>
import BananaButton from '@/components/ui/BananaButton.vue'
import { CATEGORIES_QUERY } from '@/graphql/jokes'

export default {
  name: 'PageAbout',
  components: { BananaButton },
  data() {
    return { total: null }
  },
  computed: {
    totalLabel() {
      return this.total != null ? new Intl.NumberFormat('fr-FR').format(this.total) : '2 440'
    },
  },
  apollo: {
    total: { query: CATEGORIES_QUERY, update: (d) => d.total },
  },
}
</script>

<style scoped>
.about__hero { text-align: center; padding: 56px 20px 30px; }
.about__title { font-size: clamp(2.4rem, 6.5vw, 4.4rem); }
.hl { color: var(--banana-700); }
.about__lead {
  font-family: var(--font-accent);
  font-style: italic;
  font-size: clamp(1.15rem, 2.6vw, 1.5rem);
  color: var(--ink-700);
  max-width: 560px;
  margin: 8px auto 0;
}

.story { display: flex; flex-direction: column; gap: 34px; padding: 30px 20px; }
.story__block {
  position: relative;
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-lg);
  padding: 26px 28px 24px;
  box-shadow: var(--shadow-md);
}
.story__marker {
  position: absolute;
  top: -20px; left: 24px;
  font-size: 2.2rem;
  filter: drop-shadow(2px 2px 0 var(--border-c));
}
.story__block h2 { font-size: clamp(1.4rem, 3.5vw, 2rem); margin-top: 8px; }
.story__block p { font-size: 1.08rem; line-height: 1.7; color: var(--ink-800); margin: 0; }
.story__block em { font-family: var(--font-accent); font-style: italic; }

.homage {
  background: var(--choco);
  color: var(--banana-100);
  border-radius: var(--r-xl);
  padding: 40px 36px;
  margin: 20px 0;
  border: var(--border-w) solid var(--border-c);
  box-shadow: var(--shadow-lg);
}
.homage h2 { color: var(--banana-400); font-size: clamp(1.6rem, 4vw, 2.4rem); }
.homage p { font-size: 1.08rem; line-height: 1.7; opacity: 0.92; }
.homage strong { color: var(--banana-300); }
.homage em { font-family: var(--font-accent); font-style: italic; color: var(--banana-200); }
.homage__contact {
  display: flex; align-items: center; gap: 14px;
  background: rgba(255,255,255,0.06);
  border: 1.5px dashed var(--banana-500);
  border-radius: var(--r-md);
  padding: 14px 18px;
  margin: 20px 0;
}
.homage__contact span { font-size: 1.6rem; }
.homage__contact p { margin: 0; font-size: 1rem; }
.homage__mail {
  color: var(--banana-300);
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-thickness: 2px;
  word-break: break-all;
}
.homage__mail:hover { color: var(--banana-200); }
.homage__sign { text-align: center; margin-bottom: 0 !important; font-size: 1.15rem !important; }

.tl { padding: 30px 20px; }
.tl__head { text-align: center; font-size: clamp(1.5rem, 4vw, 2.2rem); margin-bottom: 26px; }
.tl__list { list-style: none; margin: 0; padding: 0; position: relative; }
.tl__list::before { content: ''; position: absolute; left: 66px; top: 8px; bottom: 8px; width: 3px; background: var(--banana-400); border-radius: 3px; }
.tl__list li { display: flex; gap: 22px; align-items: baseline; padding: 12px 0; position: relative; }
.tl__year {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1.15rem;
  color: var(--ink-900);
  width: 56px;
  text-align: right;
  flex: none;
  position: relative;
}
.tl__year::after {
  content: ''; position: absolute; right: -21px; top: 50%; transform: translateY(-50%);
  width: 13px; height: 13px; border-radius: 50%;
  background: var(--banana-500); border: var(--border-w) solid var(--border-c);
}
.tl__evt { color: var(--ink-700); font-size: 1rem; }
.tl--dead .tl__year { color: var(--bruise); }
.tl--dead .tl__year::after { background: var(--bruise); }
.tl--revival .tl__year { color: var(--peel-green-dark); }
.tl--revival .tl__year::after { background: var(--peel-green); }
.tl--revival .tl__evt { font-weight: 700; color: var(--ink-900); }

.about__cta { text-align: center; padding: 20px 20px 50px; }

@media (max-width: 560px) {
  .tl__list::before { left: 50px; }
  .tl__year { width: 42px; font-size: 1rem; }
  .about__hero { padding: 40px 4px 24px; }
  .story { gap: 28px; padding: 24px 4px; }
  .story__block { padding: 22px 20px 20px; }
  .homage { padding: 30px 22px; border-radius: var(--r-lg); }
  .homage__contact { flex-direction: column; text-align: center; gap: 8px; }
  .tl, .homage-wrap { padding-left: 4px; padding-right: 4px; }
}
</style>
